import { useRef, useCallback, Fragment } from 'react';
import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { PostType } from '../memeverse/memeverse_types';
import { getUsersMyReplies } from '../../utils/apis/memeverse';
import Meme from '../memeverse/feed/Meme';
import { useAuth } from '../../hooks/useAuth';
import './../memeverse/feed/MemeReply.css';

const MvReplies = ({ userId }) => {
  const { authUser } = useAuth();

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } =
    useInfiniteQuery({
      queryKey: ['userReplies', userId],
      queryFn: async ({ pageParam = 1 }) => {
        try {
          const resp = await getUsersMyReplies(
            authUser?.uid,
            userId,
            10,
            pageParam
          );
          if (resp.ok) {
            const data = await resp.json();
            return {
              posts: data.posts as PostType[],
              pagination: data.pagination,
            };
          }
          return { posts: [], pagination: null };
        } catch (err) {
          console.error('Error fetching posts:', err);
          return { posts: [], pagination: null };
        }
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (lastPage.pagination && lastPage.pagination.has_next) {
          return lastPage.pagination.next_page;
        }
        return undefined;
      },
    });

  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetching) {
          fetchNextPage();
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, hasNextPage, isFetching, fetchNextPage]
  );

  const posts = data?.pages.flatMap((page) => page.posts) || [];

  return (
    <Box sx={{
      maxWidth: 'md',
      margin: 'auto',
      my: 4,
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    }}>
      {isLoading && posts.length === 0 ? (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          height='200px'
        >
          <CircularProgress />
        </Box>
      ) : posts.length === 0 ? (
        <Typography variant='body1' align='center' sx={{ mt: 2 }}>
          No entries found
        </Typography>
      ) : (
        <>
          {posts.map((post, index) => (
            <Fragment key={`${post.docId}-${index}`}>
              <Meme post={post} />
              <Divider sx={{ my: 1 }} />
            </Fragment>
          ))}

          {hasNextPage && (
            <Box
              ref={loadMoreTriggerRef}
              sx={{
                height: '20px',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {isFetching && <CircularProgress size={24} />}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default MvReplies;
